<!-- HM switch button - switch button that handles multiple values as array (based on CoreUI.io cSwitch) -->
<template>
  <div>
    <label :class="classList">
      <input
        :id="id"
        :type="type"
        :checked="isChecked"
        :disabled="disabled"
        :required="required"
        :name="name"
        :value="value"
        class="switch-input form-check-input"
        true-value="value"
        false-value="uncheckedValue"
        @change="handleChange"
      >
      <span :class="dataClassValue" :data-checked="dataOnValue" :data-unchecked="dataOffValue"></span>
    </label>
    <span :class="classLabelList">
      {{label}}
      <slot></slot>
    </span>
  </div>
</template>

<script>
export default {
  name: 'HMSwitch',
  model: {
    prop: 'modelChecked',
    event: 'change'
  },
  props: {
    id: {
      type: String,
      default: function () {
        return 'switch-id-' + this._uid;
      },
    },
    type: {
      type: String,
      default: 'checkbox',
      validator: value => ['checkbox', 'radio'].indexOf(value) !== -1
    },
    color: {
      type: String,
      default: 'primary'
    },
    label: {
      type: String,
      default: null
    },
    labelClass: {
      type: Object,
      default: null
    },
    outline: {
      type: [Boolean, String],
      default: null,
      validator: value => [false, true, '', 'alt'].indexOf(value) !== -1
    },
    size: {
      type: String,
      default: null,
      validator: value => ['', 'lg', 'sm'].indexOf(value) !== -1
    },
    checked: {
      type: Boolean,
      default: false
    },
    modelChecked: {
      default: undefined
    },
    value: {
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    onChange: {
      type: Function
    },
    uncheckedValue: {
      default: false
    },
    variant: {
      type: String,
      default: null,
      validator: value => [null, '3d', 'pill'].indexOf(value) !== -1
    },
    dataType: {
      type: String,
      default: null,
      validator: value => [null, 'onoff', 'yesno', 'check', 'radio'].indexOf(value) !== -1
    },
    dataOn: {
      type: String,
      default: null
    },
    dataOff: {
      type: String,
      default: null
    },
    dataClass: {
      type: String,
      default: null
    }
  },
  data: function () {
    return {
      checkedData: this.checked ? this.value : this.uncheckedValue,
      checkedModel: null,
      dataConfig: {
        onoff: {
          dataOn: 'On',
          dataOff: 'Off',
          dataClass: ''
        },
        yesno: {
          dataOn: 'Yes',
          dataOff: 'No',
          dataClass: ''
        },
        check: {
          dataOn: '\uf00c',
          dataOff: '\uf00d',
          dataClass: 'fa' // requires class="fa" to show FontAwesome check
        },
        radio: {
          dataOn: '\uf00c',
          dataOff: '\uf00d',
          dataClass: 'fa' // requires class="fa" to show FontAwesome check
        }
      }
    }
  },
  computed: {
    classList() {
      return [
        'switch',
        'switch-label',
        'switch-align',
        'form-check-label',
        this.size ? `switch-${this.size}` : '',
        this.variant ? `switch-${this.variant}` : '',
        `switch${this.outline ? '-outline' : ''}-${this.color}${this.outline === 'alt' ? '-alt' : ''}`
      ]
    },
    classLabelList() {
      // build class list for label, add 'text-danger' class if 'is-invalid' is set
      return { 'pl-2': true, 'text-danger': this.labelClass && this.labelClass['is-invalid'], ...this.labelClass };
    },
    dataOnValue() {
      return this.dataOn || (this.dataType && this.dataConfig[this.dataType].dataOn) || this.dataConfig.onoff.dataOn;
    },
    dataOffValue() {
      return this.dataOff || (this.dataType && this.dataConfig[this.dataType].dataOff) || this.dataConfig.onoff.dataOff;
    },
    dataClassValue() {
      return [
        'switch-slider',
        this.dataClass || (this.dataType && this.dataConfig[this.dataType].dataClass) || this.dataConfig.onoff.dataClass
      ]
    },
    isChecked() {
      if (this.modelChecked === undefined) {
        return this.checkedData === this.value
      }
      if (this.modelChecked instanceof Array) {
        return this.value !== '' ? this.modelChecked.indexOf(this.value) >= 0 : this.modelChecked.length === 0
      }
      return this.modelChecked === this.value
    }
  },
  mounted() {
    this.toggle(this.checked || this.isChecked);
  },
  methods: {
    handleChange(event) {
      this.toggle(event.target.checked);
      this.$emit('change', this.checkedModel ? this.checkedModel : this.checkedData);
    },
    toggle(checked) {
      this.checkedModel = this.modelChecked; // can't modify modelChecked so use checkedModel data variable

      if (this.checkedModel === undefined) {
        this.checkedData = checked ? this.value : this.uncheckedValue
      } else if (this.checkedModel instanceof Array) {
        if (this.value === '') {
          this.checkedModel.splice(0, this.checkedModel.length);
        } else {
          let idx = this.checkedModel.indexOf(this.value);
          if (checked && idx === -1) this.checkedModel.push(this.value);
          if (!checked && idx >= 0) this.checkedModel.splice(idx, 1);
        }
      } else {
        this.checkedModel = checked ? this.value : this.uncheckedValue
      }
    }
  },
}
</script>

<style scoped>
.switch-align {
  vertical-align: middle;
}
</style>